<template>
  <div
    :id="groupId"
    ref="radioGroupRef"
    :class="[
      ns.b('group'),
      { 'has-gutter': gutter || gutter === '' },
      // { 'has-angle-bracket': angleBracket },
    ]"
    role="radiogroup"
    :style="computedStyle"
    :aria-label="!isLabeledByFormItem ? label || 'radio-group' : undefined"
    :aria-labelledby="isLabeledByFormItem ? formItem!.labelId : undefined"
  >
    <slot />
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  onMounted,
  provide,
  reactive,
  ref,
  toRefs,
  watch,
} from 'vue'
import { get, isEqual } from 'lodash-unified'
import { useFormItem, useFormItemInputId } from '@jdesign-vue/components/form'
import { UPDATE_MODEL_EVENT } from '@jdesign-vue/constants'
import { useId, useNamespace } from '@jdesign-vue/hooks'
import { debugWarn, isString } from '@jdesign-vue/utils'
import { radioGroupEmits, radioGroupProps } from './radio-group'
import { radioGroupKey } from './constants'

import type { RadioGroupProps } from './radio-group'

defineOptions({
  name: 'JdRadioGroup',
})

const props = defineProps(radioGroupProps)
const emit = defineEmits(radioGroupEmits)

const ns = useNamespace('radio')
const radioId = useId()
const radioGroupRef = ref<HTMLDivElement>()
const { formItem } = useFormItem()
const { inputId: groupId, isLabeledByFormItem } = useFormItemInputId(props, {
  formItemContext: formItem,
})

const getRawValue = (value: string | number | boolean) => {
  if (!props.rawValues || props.rawValues.length <= 0) return

  for (let i = props.rawValues.length - 1; i >= 0; i--) {
    const rawValue = props.rawValues[i]
    if (isEqual(get(rawValue, props.valueKey as string), value)) {
      return rawValue
    }
  }
}

const changeEvent = (value: RadioGroupProps['modelValue']) => {
  emit(UPDATE_MODEL_EVENT, value)
  nextTick(() => emit('change', value))
  nextTick(() => emit('raw-change', getRawValue(value)))
}

onMounted(() => {
  const radios =
    radioGroupRef.value!.querySelectorAll<HTMLInputElement>('[type=radio]')
  const firstLabel = radios[0]
  if (!Array.from(radios).some((radio) => radio.checked) && firstLabel) {
    firstLabel.tabIndex = 0
  }
})

const name = computed(() => {
  return props.name || radioId.value
})

const computedStyle = computed(() => {
  if (isString(props.gutter) && props.gutter !== '') {
    return {
      [ns.cssVarName('radio-button-gutter')]: props.gutter,
    }
  }
  return ''
})

provide(
  radioGroupKey,
  reactive({
    ...toRefs(props),
    changeEvent,
    name,
  })
)

watch(
  () => props.modelValue,
  () => {
    if (props.validateEvent) {
      formItem?.validate('change').catch((err) => debugWarn(err))
    }
  }
)
</script>
